/**
 * Labels for workspace video encoding (Lambda videoProcessor). Thresholds align with
 * progress PATCHes — grouped into UX steps.
 */
export const VIDEO_GENERATION_STEP_THRESHOLDS = [20, 40, 60, 82, 88, 95, 100];

export const VIDEO_GENERATION_STEPS = [
  { key: 'voice', title: 'Getting started', subtitle: 'Preparing files' },
  { key: 'cuts', title: 'Preparing your clips', subtitle: 'Assembling footage' },
  { key: 'concat', title: 'Combining clips', subtitle: 'Making the rough cut' },
  { key: 'captions', title: 'Captioning', subtitle: 'Matching voiceover and text' },
  { key: 'burn', title: 'Adding captions', subtitle: 'Rendering text on video' },
  { key: 'audio', title: 'Adding audio', subtitle: 'Adding music and effects' },
  { key: 'upload', title: 'Finishing up', subtitle: 'Saving final video' },
];

/** Extra early planning + late layering steps when AI sound effects are enabled. */
export const VIDEO_GENERATION_STEP_THRESHOLDS_WITH_SFX = [4, 20, 40, 60, 82, 88, 93, 97, 100];

export const VIDEO_GENERATION_STEPS_WITH_SFX = [
  { key: 'sfx_plan', title: 'Sound effects', subtitle: 'AI picking the right moments' },
  { key: 'voice', title: 'Getting started', subtitle: 'Preparing files' },
  { key: 'cuts', title: 'Preparing your clips', subtitle: 'Assembling footage' },
  { key: 'concat', title: 'Combining clips', subtitle: 'Making the rough cut' },
  { key: 'captions', title: 'Captioning', subtitle: 'Matching voiceover and text' },
  { key: 'burn', title: 'Adding captions', subtitle: 'Rendering text on video' },
  { key: 'audio', title: 'Adding audio', subtitle: 'Mixing voice and music' },
  { key: 'sfx', title: 'Layering SFX', subtitle: 'Placing sound effects on the timeline' },
  { key: 'upload', title: 'Finishing up', subtitle: 'Saving final video' },
];

/**
 * No-script flow (lambda `generateNoScriptVideo`): no voiceover, captions or audio.
 * Lambda progress: 5, 15 (start) -> 25, 40 (download) -> 50 (concat) -> 70 (pacing/trim) -> 90 (upload) -> 100.
 * Keep in sync with mobile/src/utils/videoGenerationSteps.js.
 */
export const NO_SCRIPT_STEP_THRESHOLDS = [25, 50, 70, 90, 100];

export const NO_SCRIPT_STEPS = [
  { key: 'cuts', title: 'Preparing your clips', subtitle: 'Assembling footage' },
  { key: 'download', title: 'Preparing clips', subtitle: 'Getting your footage ready' },
  { key: 'concat', title: 'Combining clips', subtitle: 'Making the rough cut' },
  { key: 'timing', title: 'Fine-tuning', subtitle: 'Applying pacing and length' },
  { key: 'upload', title: 'Finishing up', subtitle: 'Saving final video' },
];

export function getVideoGenerationStepsConfig(includeAiSoundEffectsOrOptions = false) {
  const opts =
    includeAiSoundEffectsOrOptions && typeof includeAiSoundEffectsOrOptions === 'object'
      ? includeAiSoundEffectsOrOptions
      : { includeAiSoundEffects: includeAiSoundEffectsOrOptions === true };
  if (opts.noScript === true) {
    return { steps: NO_SCRIPT_STEPS, thresholds: NO_SCRIPT_STEP_THRESHOLDS };
  }
  const includeAiSoundEffects = opts.includeAiSoundEffects === true;
  const textOnScreen = opts.textOnScreen === true;

  const base = includeAiSoundEffects
    ? { steps: VIDEO_GENERATION_STEPS_WITH_SFX, thresholds: VIDEO_GENERATION_STEP_THRESHOLDS_WITH_SFX }
    : { steps: VIDEO_GENERATION_STEPS, thresholds: VIDEO_GENERATION_STEP_THRESHOLDS };

  if (!textOnScreen) return base;

  return {
    ...base,
    steps: base.steps.map((step) =>
      step.key === 'cuts'
        ? { ...step, title: 'Adding text on screen', subtitle: 'Overlaying your text' }
        : step
    ),
  };
}

/**
 * Single step to show for the current progress value.
 * @param {number} progress 0–100
 * @param {{ includeAiSoundEffects?: boolean, textOnScreen?: boolean }} [options]
 */
export function getCurrentVideoGenerationStep(progress, options = {}) {
  return getVideoGenerationStepProgress(progress, options).step;
}

/**
 * Map global encoding progress (0–100) to the active UX step and a per-step bar (0–100).
 * @param {number} progress 0–100
 * @param {{ includeAiSoundEffects?: boolean, textOnScreen?: boolean }} [options]
 */
export function getVideoGenerationStepProgress(progress, options = {}) {
  const { steps, thresholds: th } = getVideoGenerationStepsConfig(options);
  const p = Math.min(100, Math.max(0, Number(progress) || 0));
  const lastIdx = steps.length - 1;

  if (p >= 100) {
    return {
      stepIndex: lastIdx,
      stepProgress: 100,
      step: { ...steps[lastIdx] },
      totalSteps: steps.length,
    };
  }

  for (let i = 0; i < steps.length; i++) {
    const start = i === 0 ? 0 : th[i - 1];
    const end = th[i];
    if (p < end) {
      const range = end - start;
      const stepProgress = range > 0 ? ((p - start) / range) * 100 : 0;
      return {
        stepIndex: i,
        stepProgress: Math.min(100, Math.max(0, stepProgress)),
        step: { ...steps[i] },
        totalSteps: steps.length,
      };
    }
  }

  return {
    stepIndex: lastIdx,
    stepProgress: 100,
    step: { ...steps[lastIdx] },
    totalSteps: steps.length,
  };
}
